import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Forklift,
  Image as ImageIcon,
  LayoutGrid,
  Receipt,
  TrendingDown,
  Wrench,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { EditableSelect } from '@/components/inventory/EditableItemField';
import { EquipmentStatusBadge } from '@/components/equipment/EquipmentBadges';
import { EquipmentOverviewTab } from '@/components/equipment/EquipmentOverviewTab';
import { EquipmentPhotosTab } from '@/components/equipment/EquipmentPhotosTab';
import { EquipmentPurchaseTab } from '@/components/equipment/EquipmentPurchaseTab';
import { EquipmentDepreciationTab } from '@/components/equipment/EquipmentDepreciationTab';
import { EquipmentMaintenanceTab } from '@/components/equipment/EquipmentMaintenanceTab';
import { EquipmentDocumentsTab } from '@/components/equipment/EquipmentDocumentsTab';
import { useEquipment, useEquipmentFiles, useUpdateEquipment } from '@/features/equipment/hooks';
import {
  DISPOSAL_METHOD_LABELS,
  EQUIPMENT_CATEGORY_META,
  EQUIPMENT_STATUS_OPTIONS,
  canEditEquipment,
  canLogEquipmentMaintenance,
  equipmentCategoryLabel,
  isEquipmentCategory,
  type DisposalMethod,
} from '@/features/equipment/model';
import { useAuth } from '@/app/AuthProvider';
import { formatDateOnly } from '@/lib/formatters';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'photos', label: 'Photos', icon: ImageIcon },
  { id: 'purchase', label: 'Purchase & warranty', icon: Receipt },
  { id: 'depreciation', label: 'Depreciation', icon: TrendingDown },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench },
  { id: 'documents', label: 'Documents', icon: FileText },
] as const;

type TabId = (typeof TABS)[number]['id'];

function isTabId(value: string | null): value is TabId {
  return !!value && TABS.some((t) => t.id === value);
}

export function EquipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const { role } = useAuth();
  const canEdit = canEditEquipment(role);
  const canLog = canLogEquipmentMaintenance(role);
  const { data: equipment, isLoading, error } = useEquipment(id);
  const { data: files } = useEquipmentFiles(id);
  const update = useUpdateEquipment();

  if (error) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader title="Equipment" />
        <Card>
          <CardContent className="pt-6">
            <EmptyState icon={Forklift} title="We couldn't find that equipment" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading || !equipment) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader title="Loading equipment…" />
        <Card>
          <CardContent className="space-y-3 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabParam = params.get('tab');
  const active: TabId = isTabId(tabParam) ? tabParam : 'overview';
  const selectTab = (tab: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next, { replace: true });
  };
  const openTab = (tab: string) => {
    selectTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const photoCount = (files ?? []).filter((f) => f.kind === 'photo').length;
  const documentCount = (files ?? []).length - photoCount;
  const counts: Partial<Record<TabId, number>> = { photos: photoCount, documents: documentCount };

  return (
    <div className="space-y-6">
      <BackLink category={equipment.category} />
      <PageHeader
        title={equipment.name}
        meta={
          <>
            <span className="font-mono text-xs">{equipment.asset_tag}</span>
            {' · '}
            {equipmentCategoryLabel(equipment.category)}
            {equipment.equipment_type && ` · ${equipment.equipment_type}`}
          </>
        }
        actions={
          <EditableSelect
            value={equipment.status}
            options={EQUIPMENT_STATUS_OPTIONS}
            ariaLabel="Status"
            canEdit={canEdit}
            density="kv"
            align="right"
            display={<EquipmentStatusBadge status={equipment.status} />}
            onSave={async (next) => {
              await update.mutateAsync({ id: equipment.id, patch: { status: next } });
            }}
          />
        }
      />

      {equipment.disposed_on && (
        <div className="rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/15 px-4 py-3 text-sm text-fresh-salmon-dark">
          Disposed {formatDateOnly(equipment.disposed_on)}
          {equipment.disposal_method &&
            ` · ${DISPOSAL_METHOD_LABELS[equipment.disposal_method as DisposalMethod] ?? equipment.disposal_method}`}
          . Depreciation stopped that month.
        </div>
      )}

      <Tabs value={active} onValueChange={selectTab}>
        <TabsList className="h-auto flex-wrap gap-1 bg-secondary p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="gap-1.5 data-[state=active]:shadow-sm">
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
              {counts[t.id] ? (
                <span className="rounded-full bg-background/70 px-1.5 text-[11px] tabular-nums text-muted-foreground">
                  {counts[t.id]}
                </span>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <EquipmentOverviewTab equipment={equipment} canEdit={canEdit} onOpenTab={openTab} />
        </TabsContent>
        <TabsContent value="photos" className="mt-6">
          <EquipmentPhotosTab equipment={equipment} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="purchase" className="mt-6">
          <EquipmentPurchaseTab equipment={equipment} canEdit={canEdit} onOpenTab={openTab} />
        </TabsContent>
        <TabsContent value="depreciation" className="mt-6">
          <EquipmentDepreciationTab equipment={equipment} canEdit={canEdit} onOpenTab={openTab} />
        </TabsContent>
        <TabsContent value="maintenance" className="mt-6">
          <EquipmentMaintenanceTab equipment={equipment} canLog={canLog} />
        </TabsContent>
        <TabsContent value="documents" className="mt-6">
          <EquipmentDocumentsTab equipment={equipment} canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BackLink({ category }: { category?: string }) {
  const scoped = category && isEquipmentCategory(category) ? category : null;
  return (
    <Link
      to={scoped ? `/catalog/equipment/category/${scoped}` : '/catalog/equipment'}
      className="inline-flex min-h-9 items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to {scoped ? EQUIPMENT_CATEGORY_META[scoped].label.toLowerCase() : 'equipment'}
    </Link>
  );
}
